/*
 * Resizes and re-encodes a vehicle picture in the browser before upload (#100), producing what the
 * backend's compressor would: a JPEG fitted into the box, named .jpg. A JPEG within 1920x1080 whose
 * EXIF Orientation is missing or 1 then skips the resize on the server
 * (bettercargo-inventorydb-backend core/vehicles/models.py, _compress_image), which is what keeps a
 * phone photo from running an App Engine instance out of memory. Any other tag is decoded and
 * re-encoded there, which is the work this skips, so that file is converted here instead (#118).
 */
import { ImageUploadSettings, resolveImageUploadSettings } from './settings';
import { decodeUpright, ImageDecoder } from './decodeUpright';

// Below this the artefacts show on car paint and sky. The byte cap is a guideline, so it stops here.
export const QUALITY_FLOOR = 0.6;
const QUALITY_STEP = 0.05;

// The Orientation tag is in the first EXIF IFD. Past this, an unread tag is treated as unknown.
const JPEG_HEADER_BYTES = 64 * 1024;
const EXIF_ORIENTATION_TAG = 0x0112;
const EXIF_ASCII = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

/*
 * The backend's rule: fit into the box keeping the aspect ratio, only when a side exceeds it, and
 * truncate like Python's int().
 */
export function fitInBox(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) return { width, height };
  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return { width: Math.max(1, Math.floor(width * ratio)), height: Math.max(1, Math.floor(height * ratio)) };
}

/*
 * Orientation from one APP1 payload, or 1 when this Exif block has no tag (the EXIF default).
 * False: the segment is not Exif. Null: it is Exif, but the tag cannot be read from these bytes.
 */
function exifOrientation(
  bytes: Uint8Array,
  view: DataView,
  payload: number,
  segmentEnd: number
): number | null | false {
  const limit = Math.min(segmentEnd, bytes.length);
  if (limit - payload < EXIF_ASCII.length) return segmentEnd > bytes.length ? null : false;
  for (let index = 0; index < EXIF_ASCII.length; index += 1) {
    if (bytes[payload + index] !== EXIF_ASCII[index]) return false;
  }

  const tiff = payload + EXIF_ASCII.length;
  if (limit - tiff < 8) return null;
  const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
  const big = bytes[tiff] === 0x4d && bytes[tiff + 1] === 0x4d;
  if ((!little && !big) || view.getUint16(tiff + 2, little) !== 0x002a) return null;

  const ifdOffset = view.getUint32(tiff + 4, little);
  // The offset is from the TIFF header. Smaller than 8 lands inside that header.
  if (ifdOffset < 8) return null;
  let entry = tiff + ifdOffset;
  if (entry + 2 > limit) return null;
  const count = view.getUint16(entry, little);
  entry += 2;
  for (let index = 0; index < count; index += 1) {
    if (entry + 12 > limit) return null;
    if (view.getUint16(entry, little) === EXIF_ORIENTATION_TAG) {
      const type = view.getUint16(entry + 2, little);
      const components = view.getUint32(entry + 4, little);
      // SHORT, one value, stored in the entry. Anything else is not the tag we know how to trust.
      if (type !== 3 || components !== 1) return null;
      return view.getUint16(entry + 8, little);
    }
    entry += 12;
  }
  return 1;
}

/*
 * EXIF Orientation from the first few KB of a JPEG, or 1 when the tag is absent. Null when the bytes
 * are not a JPEG, or the header is cut off before the tag can be known: guessing 1 there would send a
 * turned picture unchanged (#118).
 */
export function jpegOrientation(bytes: Uint8Array): number | null {
  if (bytes.length < 2 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 2;
  while (offset + 1 < bytes.length) {
    if (bytes[offset] !== 0xff) return null;
    offset += 1;
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) return null;
    const marker = bytes[offset];
    offset += 1;
    // EOI or SOS: metadata is over, and no Orientation tag was in it.
    if (marker === 0xd9 || marker === 0xda) return 1;
    // Markers with no length: TEM, RST, SOI.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue;
    if (offset + 2 > bytes.length) return null;
    const segmentLength = view.getUint16(offset, false);
    if (segmentLength < 2) return null;
    const segmentEnd = offset + segmentLength;
    if (marker === 0xe1) {
      const orientation = exifOrientation(bytes, view, offset + 2, segmentEnd);
      if (orientation !== false) return orientation;
    }
    if (segmentEnd > bytes.length) return null;
    offset = segmentEnd;
  }
  return null;
}

// Already what the server would keep untouched: send it as-is, without a second lossy pass.
// Only Orientation 1, or a missing tag (which the reader reports as 1), is sent as-is. The server re-encodes
// 2 to 8. It leaves 0 and 9 and above alone, but they are converted here too, which is the safe side.
export function canSendAsIs(
  file: { type: string; size: number },
  width: number,
  height: number,
  settings: ImageUploadSettings,
  orientation: number | null
): boolean {
  return (
    file.type === 'image/jpeg' &&
    orientation === 1 &&
    width <= settings.maxWidth &&
    height <= settings.maxHeight &&
    (settings.maxBytes === null || file.size <= settings.maxBytes)
  );
}

/*
 * The qualities to try, in order: the setting, then down in 0.05 steps to the floor when a byte cap is
 * set. A setting already at or below the floor is tried alone.
 */
export function qualitySteps(quality: number, hasByteCap: boolean): number[] {
  const steps = [quality];
  if (!hasByteCap) return steps;
  for (let next = quality - QUALITY_STEP; next >= QUALITY_FLOOR - 1e-9; next -= QUALITY_STEP) {
    steps.push(Math.round(next * 100) / 100);
  }
  return steps;
}

/*
 * The backend's naming: a .jpg or .jpeg name is kept, anything else gets its extension replaced
 * (Python's os.path.splitext, which ignores leading dots).
 */
export function jpegFileName(name: string): string {
  if (/\.jpe?g$/i.test(name)) return name;
  const leadingDots = /^\.*/.exec(name)?.[0].length ?? 0;
  const dot = name.lastIndexOf('.');
  const base = dot >= leadingDots && dot > 0 ? name.slice(0, dot) : name;
  return `${base}.jpg`;
}

const megabytes = (bytes: number) => `${(bytes / 1000000).toFixed(2)} MB`;

function describeSettings(settings: ImageUploadSettings): string {
  const cap = settings.maxBytes === null ? 'no byte cap' : `byte cap ${settings.maxBytes}`;
  return `box ${settings.maxWidth}x${settings.maxHeight}, quality ${settings.quality}, ${cap}`;
}

function toJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned no image'))),
      'image/jpeg',
      quality
    );
  });
}

// Resolved per call, so a browser without createImageBitmap fails inside prepareForUpload's try.
const browserDecoder: ImageDecoder = (source, options) => createImageBitmap(source, options);

/**
 * Returns the file to upload for a vehicle picture. With the switch off, or on any error, that is the
 * original file, exactly as before #100. Never throws. `decoder` is for tests.
 */
export async function prepareForUpload(
  file: File,
  settings: ImageUploadSettings = resolveImageUploadSettings(),
  decoder: ImageDecoder = browserDecoder
): Promise<File> {
  if (!settings.enabled) return file;

  const started = performance.now();
  let bitmap: ImageBitmap | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    if (typeof createImageBitmap !== 'function') {
      throw new Error('this browser has no createImageBitmap');
    }
    // Displayed size is not enough: the server still decodes and re-encodes a non-identity tag.
    // The canvas below carries no EXIF, so the converted file is upright. One header read, no second decode.
    // Only a JPEG can be sent as-is, so nothing else is read.
    const orientation =
      file.type === 'image/jpeg'
        ? jpegOrientation(new Uint8Array(await file.slice(0, JPEG_HEADER_BYTES).arrayBuffer()))
        : null;
    // The canvas output carries no EXIF, so a phone photo's rotation has to be applied while decoding.
    // On a browser that rejects 'from-image', decodeUpright finds another upright path or throws (#114).
    const decoded = await decodeUpright(file, orientation, decoder);
    ({ bitmap } = decoded);
    const path = `decoded via ${decoded.path}`;

    if (canSendAsIs(file, bitmap.width, bitmap.height, settings, orientation)) {
      console.info(
        `Image upload: ${file.name} sent as-is, ${megabytes(file.size)}, ${bitmap.width}x${bitmap.height}, ` +
          `${path} (${describeSettings(settings)}).`
      );
      return file;
    }

    const target = fitInBox(bitmap.width, bitmap.height, settings.maxWidth, settings.maxHeight);
    canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('no 2D canvas context');
    }
    // JPEG has no transparency: put it on white, as the backend does.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, target.width, target.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, target.width, target.height);

    let blob: Blob | undefined;
    let usedQuality = settings.quality;
    for (const quality of qualitySteps(settings.quality, settings.maxBytes !== null)) {
      // Sequential on purpose: each step is tried only when the previous one is over the cap.
      blob = await toJpegBlob(canvas, quality);
      usedQuality = quality;
      if (settings.maxBytes === null || blob.size <= settings.maxBytes) break;
    }
    if (!blob) {
      throw new Error('no JPEG was produced');
    }

    const converted = new File([blob], jpegFileName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
    console.info(
      `Image upload: ${file.name} ${megabytes(file.size)} ${bitmap.width}x${bitmap.height} -> ` +
        `${converted.name} ${megabytes(converted.size)} ${target.width}x${target.height}, ` +
        `quality used ${usedQuality}, ${Math.round(performance.now() - started)} ms, ${path} ` +
        `(${describeSettings(settings)}).`
    );
    return converted;
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    console.warn(`Image upload: sending ${file.name} unchanged, the conversion failed: ${reason}`);
    return file;
  } finally {
    bitmap?.close();
    if (canvas) {
      // Releases the backing store now; iOS Safari caps total canvas memory per page.
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
